import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type ToastSeverity = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  autoHideDuration?: number;
}

interface UiState {
  toasts: Toast[];
  isGlobalLoading: boolean;
  sidebarOpen: boolean;
  themeMode: "light" | "dark";
}

const getInitialTheme = (): "light" | "dark" => {
  try {
    const stored = localStorage.getItem("pinehood-theme");
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  return "dark";
};

const initialState: UiState = {
  toasts: [],
  isGlobalLoading: false,
  sidebarOpen: true,
  themeMode: getInitialTheme(),
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    showToast: (
      state,
      action: PayloadAction<{
        message: string;
        severity?: ToastSeverity;
        autoHideDuration?: number;
      }>,
    ) => {
      const toast: Toast = {
        id: Date.now().toString(),
        message: action.payload.message,
        severity: action.payload.severity || "info",
        autoHideDuration: action.payload.autoHideDuration ?? 5000,
      };
      state.toasts.push(toast);
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isGlobalLoading = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleTheme: (state) => {
      state.themeMode = state.themeMode === "light" ? "dark" : "light";
      try {
        localStorage.setItem("pinehood-theme", state.themeMode);
      } catch {}
    },
  },
});

export const {
  showToast,
  removeToast,
  clearToasts,
  setGlobalLoading,
  toggleSidebar,
  setSidebarOpen,
  toggleTheme,
} = uiSlice.actions;

export default uiSlice.reducer;
